import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowRightIcon, XIcon } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router';
import z from 'zod';

import { Alert, AlertDescription, AlertTitle } from '@/components/core/alert';
import { Button } from '@/components/core/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/core/card';
import { Form } from '@/components/core/form';
import { SelectItem } from '@/components/core/select';
import { SelectFormField } from '@/components/forms/select-form-field';
import { TextFormField } from '@/components/forms/text-form-field';
import { Header, HeaderActions, HeaderTitle } from '@/components/header';
import { Page, PageContent } from '@/components/layout';
import { useGateways } from '@/gateways/GatewayProvider';
import { useGatewayMutation } from '@/shared/hooks/queries';

import { CsvFileField } from '../shared/csv-file-field';
import { computeSha256Hex } from '../shared/inputs-upload';

const schema = z.object({
  name: z.string().min(1, 'Enter a simulation name'),
  type: z.literal('SHIPPING'),
  inputsFile: z.custom<File>((value) => value instanceof File, {
    message: 'Pick a CSV file with the inputs',
  }),
});

type FormValues = z.infer<typeof schema>;

type Step = 'idle' | 'hashing' | 'creating' | 'uploading' | 'awaiting-ingest';

const STEP_LABELS: Record<Exclude<Step, 'idle'>, string> = {
  hashing: 'Hashing CSV...',
  creating: 'Creating simulation...',
  uploading: 'Uploading to S3...',
  'awaiting-ingest': 'Waiting for ingest...',
};

export function CreateSimulationPage() {
  const navigate = useNavigate();
  const { simulationGateway } = useGateways();
  const [step, setStep] = useState<Step>('idle');

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      type: 'SHIPPING',
    },
  });

  const { mutate: submitSimulation, isPending: isSubmitting } = useGatewayMutation<
    string,
    FormValues
  >({
    mutationFn: async (data: FormValues) => {
      setStep('hashing');
      const inputsHash = await computeSha256Hex(data.inputsFile);

      setStep('creating');
      const { simulation, presignedUpload } = await simulationGateway.createSimulation({
        name: data.name,
        inputsHash,
      });

      setStep('uploading');
      await simulationGateway.uploadSimulationInputsFile(presignedUpload, data.inputsFile);

      setStep('awaiting-ingest');
      await simulationGateway.waitForSimulationInputsUpload(simulation.id);

      return simulation.id;
    },
    onSuccess: (simulationId) => {
      void navigate(`/simulations/${simulationId}`);
    },
    onSettled: () => {
      setStep('idle');
    },
    successMessage: 'Simulation inputs uploaded',
  });

  const onSubmit = (data: FormValues) => {
    void submitSimulation(data);
  };

  const inputsFile = form.watch('inputsFile');

  return (
    <Page>
      <Header>
        <Button asChild variant="ghost" size="icon" className="col-start-1 row-start-1 -ml-2">
          <Link to="/simulations" aria-label="Cancel">
            <XIcon className="size-5" />
          </Link>
        </Button>
        <HeaderTitle>New simulation</HeaderTitle>
        <HeaderActions>
          <Button
            type="submit"
            form="create-simulation-form"
            loading={isSubmitting}
            disabled={isSubmitting || !inputsFile}
          >
            Continue
            <ArrowRightIcon />
          </Button>
        </HeaderActions>
      </Header>
      <PageContent variant="column">
        <div className="mx-auto flex w-full max-w-3xl flex-col gap-4">
          <Form {...form}>
            <form
              id="create-simulation-form"
              onSubmit={form.handleSubmit(onSubmit)}
              className="contents"
            >
              <Card>
                <CardHeader>
                  <CardTitle>Name</CardTitle>
                </CardHeader>
                <CardContent className="grid gap-4 @md:grid-cols-2">
                  <TextFormField
                    control={form.control}
                    name="name"
                    label="Experiment name"
                    placeholder="Enter"
                  />
                  <SelectFormField
                    control={form.control}
                    name="type"
                    label="Type"
                    className="w-full"
                  >
                    <SelectItem value="SHIPPING">Shipping</SelectItem>
                  </SelectFormField>
                </CardContent>
              </Card>

              <Card>
                <CardContent>
                  <CsvFileField control={form.control} name="inputsFile" />
                </CardContent>
              </Card>

              {step !== 'idle' && (
                <Alert variant="info">
                  <AlertTitle>{STEP_LABELS[step]}</AlertTitle>
                  <AlertDescription>
                    Hold on while the CSV uploads and the server confirms ingest.
                  </AlertDescription>
                </Alert>
              )}
            </form>
          </Form>
        </div>
      </PageContent>
    </Page>
  );
}
